import React, { Fragment, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import { Drawer, Divider, Paper, Avatar, Typography, Hidden, IconButton, Tooltip } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import useRouter from 'utils/Router/useRouter';
import Navigation from 'components/Navigation';
import AppVersion from 'components/AppVersion';
import navigationConfig from './navigationConfig';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  content: {
    padding: theme.spacing(2),
    // So the release line can sit at the foot of the menu.
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    boxSizing: 'border-box'
  },
  version: {
    marginTop: 'auto',
    paddingTop: theme.spacing(3)
  },
  contentCollapsed: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1)
  },
  toggle: {
    display: 'flex',
    justifyContent: 'flex-end',
    minHeight: 32
  },
  toggleCollapsed: {
    justifyContent: 'center'
  },
  profile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: 'fit-content'
  },
  avatar: {
    width: 60,
    height: 60,
    transition: theme.transitions.create(['width', 'height'])
  },
  avatarCollapsed: {
    width: 40,
    height: 40
  },
  name: {
    marginTop: theme.spacing(1)
  },
  divider: {
    marginTop: theme.spacing(2)
  },
  navigation: {
    marginTop: theme.spacing(2)
  }
}));

const NavBar = props => {
  const {
    openMobile,
    onMobileClose,
    collapsed,
    onToggleCollapsed,
    onExpand,
    className,
    ...rest
  } = props;

  const classes = useStyles();
  const [t] = useTranslation();
  const router = useRouter();
  const session = useSelector(state => state.userSession);

  useEffect(() => {
    if (openMobile) {
      onMobileClose && onMobileClose();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.location.pathname]);

  /**
   * The collapsed rail is icons only, so the name, the bio and the section
   * headings go — they have no room and nothing to say at 72px.
   *
   * `isCollapsed` is passed rather than read from `collapsed` directly: the
   * mobile drawer renders the same content at full width and must never
   * collapse, since it is already an overlay and wins no space by shrinking.
   * That drawer therefore gets no toggle either — a button that shrinks
   * something the caller does not render is a control for nothing.
   */
  const navbarContent = (isCollapsed, canCollapse) => (
    <div className={clsx(classes.content, isCollapsed && classes.contentCollapsed)}>
      {canCollapse && onToggleCollapsed && (
        <div className={clsx(classes.toggle, isCollapsed && classes.toggleCollapsed)}>
          <Tooltip
            placement="right"
            title={t(isCollapsed ? 'general.expandNavigation' : 'general.collapseNavigation')}
          >
            <IconButton
              aria-label={t(isCollapsed ? 'general.expandNavigation' : 'general.collapseNavigation')}
              data-testid="navbar-toggle"
              onClick={onToggleCollapsed}
              size="small"
            >
              {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        </div>
      )}
      <div className={classes.profile}>
        <Avatar
          alt={t('general.avatarAlt')}
          className={clsx(classes.avatar, isCollapsed && classes.avatarCollapsed)}
          component={RouterLink}
          src={session.user.avatar}
          to="/account/general"
        />
        {!isCollapsed && (
          <Fragment>
            <Typography
              className={classes.name}
              variant="h4"
            >
              {session.user.first_name} {session.user.last_name}
            </Typography>
            <Typography variant="body2">{session.user.bio}</Typography>
          </Fragment>
        )}
      </div>
      <Divider className={classes.divider} />
      <nav className={classes.navigation}>
        {navigationConfig.map(list => (
          <Navigation
            collapsed={isCollapsed}
            component="div"
            key={list.title}
            onExpand={onExpand}
            pages={list.pages}
            title={list.title}
            availableForRoles={list.availableForRoles}
          />
        ))}
      </nav>
      <div className={classes.version}>
        <AppVersion compact={isCollapsed} />
      </div>
    </div>
  );

  return (
    <Fragment>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          <div
            {...rest}
            className={clsx(classes.root, className)}
          >
            {navbarContent(false, false)}
          </div>
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Paper
          {...rest}
          className={clsx(classes.root, className)}
          elevation={1}
          square
        >
          {navbarContent(collapsed, true)}
        </Paper>
      </Hidden>
    </Fragment>
  );
};

NavBar.propTypes = {
  className: PropTypes.string,
  collapsed: PropTypes.bool,
  onExpand: PropTypes.func,
  onMobileClose: PropTypes.func,
  onToggleCollapsed: PropTypes.func,
  openMobile: PropTypes.bool
};

NavBar.defaultProps = {
  collapsed: false
};

export default NavBar;
